import type { Theme } from '../../hooks/useTheme'
import { Sun, Moon } from 'lucide-react'
import { useLocale } from '../../i18n/context'

interface Props {
  theme: Theme
  onSetTheme: (theme: Theme) => void
  /** Sidebar-collapsed rail — compresses to a single control in the same
   *  position rather than showing both states side by side. */
  collapsed?: boolean
}

/**
 * Two-state theme control for the sidebar foot (above the user row) —
 * shows both states rather than a single button whose meaning depends on
 * the current theme (DESIGN_SYSTEM.md: the screen-header bar rework moved
 * this out of the header entirely).
 */
export default function ThemeSwitch({ theme, onSetTheme, collapsed = false }: Props) {
  const { tr } = useLocale()
  const isDark = theme === 'dark'

  if (collapsed) {
    const label = isDark ? tr.layout.switchToLight : tr.layout.switchToDark
    return (
      <button
        onClick={() => onSetTheme(isDark ? 'light' : 'dark')}
        title={label}
        aria-label={label}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', padding: 8, borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 36, minHeight: 36,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)' }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)' }}
      >
        {isDark ? <Moon size={16} /> : <Sun size={16} />}
      </button>
    )
  }

  return (
    <div
      role="radiogroup"
      aria-label={tr.layout.themeSwitchLabel}
      style={{
        display: 'flex', width: '100%', padding: 2, gap: 2,
        borderRadius: 'var(--radius-pill)', background: 'var(--bg-secondary)',
      }}
    >
      <button
        role="radio"
        aria-checked={!isDark}
        title={tr.nav.lightMode}
        aria-label={tr.nav.lightMode}
        onClick={() => onSetTheme('light')}
        style={segmentStyle(!isDark)}
      >
        <Sun size={14} />
      </button>
      <button
        role="radio"
        aria-checked={isDark}
        title={tr.nav.darkMode}
        aria-label={tr.nav.darkMode}
        onClick={() => onSetTheme('dark')}
        style={segmentStyle(isDark)}
      >
        <Moon size={14} />
      </button>
    </div>
  )
}

function segmentStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '6px 12px',
    border: 'none',
    borderRadius: 'var(--radius-pill)',
    background: active ? 'var(--bg-surface)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
    boxShadow: active ? 'var(--shadow-card)' : 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
  }
}
